/**
 * CompanyInfo — Admin settings page for the company name, mailing address,
 * phone, email, website, and payment URL that appear on every SealTech
 * invoice, PO, warranty application, and outbound email.
 *
 * Editing any field here immediately propagates to:
 *   • Invoice PDFs (FROM block, footer, Remittance block)
 *   • Deal Action Bar → WCSC Warranty modal (contractor autofill)
 *   • Purchase Order cover sheet
 *   • Invoice email body
 *
 * Route: /settings/company-info (admin-only)
 * API:   GET/PUT /api/settings/company-info
 */
import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Building2, Save, RotateCcw, Loader2 } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { Navigate } from "react-router-dom";

const FIELDS = [
  { k: "name",            label: "Company Name",       placeholder: "SealTech Building Solutions" },
  { k: "address_line1",   label: "Address (Line 1)",   placeholder: "2278 Mannatt Ct, Unit C02" },
  { k: "address_line2",   label: "Address (Line 2)",   placeholder: "" },
  { k: "city",            label: "City",               placeholder: "Castle Rock", cols: 2 },
  { k: "state",           label: "State",              placeholder: "CO",           cols: 1 },
  { k: "zip",             label: "Zip",                placeholder: "80104",        cols: 1 },
  { k: "phone",           label: "Phone",              placeholder: "(720) 715-9955" },
  { k: "email",           label: "Finance / Billing Email", placeholder: "finance@sealtechsolutions.co" },
  { k: "projects_email",  label: "Projects Email",     placeholder: "projects@sealtechsolutions.co" },
  { k: "website",         label: "Website",            placeholder: "https://sealtechsolutions.co" },
  { k: "melio_url",       label: "Melio / Online Payment URL", placeholder: "https://melio.me/SealTech" },
];

export default function CompanyInfo() {
  const { user } = useAuth();
  const [info, setInfo] = useState({});
  const [defaults, setDefaults] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [updatedAt, setUpdatedAt] = useState(null);
  const [updatedBy, setUpdatedBy] = useState(null);

  useEffect(() => {
    if (user?.role !== "admin") return;
    (async () => {
      try {
        const r = await api.get("/settings/company-info");
        setInfo(r.data.info || {});
        setDefaults(r.data.defaults || {});
        setUpdatedAt(r.data.updated_at);
        setUpdatedBy(r.data.updated_by_name);
      } catch (e) {
        toast.error(e?.response?.data?.detail || "Failed to load company info");
      } finally {
        setLoading(false);
      }
    })();
  }, [user?.role]);

  if (user && user.role !== "admin") return <Navigate to="/dashboard" replace />;

  const update = (k, v) => setInfo((prev) => ({ ...prev, [k]: v }));

  const save = async () => {
    setSaving(true);
    try {
      const r = await api.put("/settings/company-info", { info });
      setInfo(r.data.info || info);
      setUpdatedAt(r.data.updated_at);
      toast.success("Company info saved. All new PDFs and emails will use these values.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const resetField = (k) => update(k, defaults[k] || "");

  if (loading) {
    return (
      <div className="p-8 flex items-center gap-2 text-zinc-500">
        <Loader2 className="w-4 h-4 animate-spin" /> Loading company info…
      </div>
    );
  }

  return (
    <div className="p-6 md:p-8 max-w-3xl">
      <div className="flex items-center gap-3 mb-6">
        <div className="w-10 h-10 rounded-sm bg-zinc-950 text-white flex items-center justify-center">
          <Building2 className="w-5 h-5" />
        </div>
        <div>
          <div className="text-[10px] font-bold uppercase tracking-[0.15em] text-zinc-500">Settings</div>
          <h1 className="font-heading text-2xl font-bold tracking-tight text-zinc-950" data-testid="company-info-heading">
            Company Info
          </h1>
        </div>
      </div>

      <p className="text-sm text-zinc-600 mb-6 leading-relaxed">
        This is the single source of truth for your company identity across every generated document.
        Changes take effect immediately for all invoices, purchase orders, warranty applications, and
        outbound emails — no need to reprint anything, just re-download.
      </p>

      <div className="bg-white border border-zinc-200 rounded-sm p-6 space-y-4" data-testid="company-info-form">
        {FIELDS.map((f) => {
          if (f.k === "city") {
            // Render City / State / Zip as one grouped row
            return (
              <div key="csz" className="grid grid-cols-1 md:grid-cols-4 gap-3">
                {FIELDS.filter((x) => ["city", "state", "zip"].includes(x.k)).map((cf) => (
                  <label key={cf.k} className={`block ${cf.k === "city" ? "md:col-span-2" : ""}`}>
                    <div className="text-[10px] font-bold uppercase tracking-[0.1em] text-zinc-600 mb-1">{cf.label}</div>
                    <input
                      type="text"
                      className="w-full border border-zinc-300 rounded-sm px-2 py-1.5 text-sm focus:outline-none focus:border-zinc-950"
                      value={info[cf.k] || ""}
                      onChange={(e) => update(cf.k, e.target.value)}
                      placeholder={cf.placeholder}
                      data-testid={`company-info-${cf.k}`}
                    />
                  </label>
                ))}
              </div>
            );
          }
          if (f.k === "state" || f.k === "zip") return null; // rendered above
          return (
            <label key={f.k} className="block">
              <div className="flex items-center justify-between mb-1">
                <div className="text-[10px] font-bold uppercase tracking-[0.1em] text-zinc-600">{f.label}</div>
                {(info[f.k] || "") !== (defaults[f.k] || "") && (
                  <button
                    type="button"
                    onClick={() => resetField(f.k)}
                    className="text-[10px] text-zinc-500 hover:text-zinc-900 flex items-center gap-1"
                    title={`Reset to default: ${defaults[f.k] || "(empty)"}`}
                    data-testid={`company-info-reset-${f.k}`}
                  >
                    <RotateCcw className="w-3 h-3" /> Reset
                  </button>
                )}
              </div>
              <input
                type="text"
                className="w-full border border-zinc-300 rounded-sm px-2 py-1.5 text-sm focus:outline-none focus:border-zinc-950"
                value={info[f.k] || ""}
                onChange={(e) => update(f.k, e.target.value)}
                placeholder={f.placeholder}
                data-testid={`company-info-${f.k}`}
              />
            </label>
          );
        })}
      </div>

      <div className="mt-4 flex items-center justify-between text-xs text-zinc-500">
        <div>
          {updatedAt ? (
            <>Last saved {new Date(updatedAt).toLocaleString()}{updatedBy ? ` by ${updatedBy}` : ""}</>
          ) : (
            "Using built-in defaults — never saved."
          )}
        </div>
        <button
          onClick={save}
          disabled={saving}
          className="inline-flex items-center gap-1.5 px-4 py-2 bg-zinc-950 text-white text-sm font-semibold rounded-sm hover:bg-black disabled:opacity-50"
          data-testid="company-info-save-btn"
        >
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
          Save Company Info
        </button>
      </div>
    </div>
  );
}
